import { ITEM_CONDITIONS, MAX_FIELDS_PER_ITEM, type ItemCondition } from "./types.js";
import {
  MAX_TEXT_LENGTH,
  centsFromInput,
  clampName,
  clampText,
  clampTitle,
  normaliseField,
  normaliseTags,
} from "./validate.js";

/**
 * Stage 3, the way back in: whatever a chat window produced, turned into
 * proposals or into a sentence saying what is wrong with it.
 *
 * The input is a human pasting a model's reply, so it is treated as hostile in
 * the ordinary, boring sense: it may be wrapped in prose and a code fence, it
 * may be cut off, it may have the wrong types, and it may say things nobody
 * asked for. Nothing here is trusted and nothing here saves -- the output is a
 * list of proposals that the review screen shows against the current values.
 *
 * **Lenient about parts, strict about the whole.** A reply that is not JSON, or
 * has no `items`, is refused with a reason. A reply where one item's condition
 * is "mint" keeps the rest of that item and says what it dropped, because
 * throwing away eight good suggestions over one bad word would make the round
 * trip worse than typing.
 *
 * Only the keys in the schema are read. `notes`, ids of things that were not in
 * the prompt, and anything else a model volunteers are ignored, so a reply can
 * never reach a private field or another item.
 *
 * Independent of the prompt builder on purpose: the schema is described in
 * words there and enforced here, and neither imports the other.
 */

export interface EnrichProposal {
  itemId: string;
  title?: string;
  description?: string;
  condition?: ItemCondition;
  /** A name, not an id: the model has never seen one. Matched against the taxonomy at review. */
  category?: string;
  priceCents?: number;
  tags?: string[];
  fields?: { label: string; value: string }[];
}

export type EnrichImport =
  { ok: true; proposals: EnrichProposal[]; warnings: string[] } | { ok: false; reason: string };

/** The items the prompt was built from, with a name for each so a warning can say which one. */
export interface ExpectedItem {
  id: string;
  name: string;
}

/**
 * The first balanced `{...}` or `[...]` in the text, aware of strings.
 * Models wrap their JSON in "Here you go!" and a code fence; this finds it
 * without a regex that would be fooled by a brace inside a description.
 */
function findJson(text: string): string | null {
  const start = text.search(/[{[]/);
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let index = start; index < text.length; index++) {
    const char = text[index];
    if (inString) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') inString = true;
    else if (char === "{" || char === "[") depth++;
    else if (char === "}" || char === "]") {
      depth--;
      if (depth === 0) return text.slice(start, index + 1);
    }
  }
  // Never closed: the reply was cut off. Returned whole so JSON.parse can say
  // where, rather than this function guessing.
  return text.slice(start);
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** "Like new", "like-new" and "LIKE_NEW" are one condition; anything else is not. */
function conditionOf(value: unknown): ItemCondition | null {
  if (typeof value !== "string") return null;
  const key = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return key !== "" && (ITEM_CONDITIONS as readonly string[]).includes(key) ? (key as ItemCondition) : null;
}

const CONDITION_WORDS = ITEM_CONDITIONS.filter(Boolean).join(", ");

export function importEnrichment(reply: string, expected: readonly ExpectedItem[]): EnrichImport {
  if (!reply.trim()) {
    return { ok: false, reason: "Nothing was pasted. Paste the model's whole reply into the box." };
  }

  const raw = findJson(reply);
  if (raw === null) {
    return {
      ok: false,
      reason: "That does not contain any JSON. Paste the model's whole reply, braces included.",
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (problem) {
    const why = problem instanceof Error ? ` (${problem.message})` : "";
    return {
      ok: false,
      reason: `That JSON could not be read${why}. It may have been cut off; ask the model to continue or to answer again.`,
    };
  }

  // The schema asks for { items: [...] }, but a single-item reply that is just
  // the item, or a bare array, is unambiguous enough to accept.
  const entries: unknown = isRecord(parsed)
    ? (parsed["items"] ?? (parsed["id"] ? [parsed] : undefined))
    : parsed;
  if (!Array.isArray(entries)) {
    return {
      ok: false,
      reason: 'The JSON has no "items" list. It should look like { "items": [ { "id": ..., ... } ] }.',
    };
  }

  const names = new Map(expected.map((item) => [item.id, item.name]));
  const proposals: EnrichProposal[] = [];
  const warnings: string[] = [];
  const seen = new Set<string>();

  entries.forEach((entry: unknown, position) => {
    if (!isRecord(entry)) {
      warnings.push(`Entry ${position + 1} is not an object, so it was skipped.`);
      return;
    }

    const id = typeof entry["id"] === "string" ? entry["id"].trim() : "";
    const name = names.get(id);
    if (name === undefined) {
      const which = id ? `the id "${id.slice(0, 40)}"` : "no id";
      warnings.push(
        `Entry ${position + 1} has ${which}, which is not one of the items in this prompt, so it was skipped.`,
      );
      return;
    }
    if (seen.has(id)) {
      warnings.push(`"${name}" appears more than once in the reply; only the first was used.`);
      return;
    }
    seen.add(id);

    const proposal: EnrichProposal = { itemId: id };

    const title = typeof entry["title"] === "string" ? clampTitle(entry["title"]) : "";
    if (title) proposal.title = title;

    const description =
      typeof entry["description"] === "string" ? clampText(entry["description"], MAX_TEXT_LENGTH) : "";
    if (description) proposal.description = description;

    if (entry["condition"] != null && entry["condition"] !== "") {
      const condition = conditionOf(entry["condition"]);
      if (condition) proposal.condition = condition;
      else {
        const said = String(entry["condition"]).slice(0, 30);
        warnings.push(
          `"${name}": the condition "${said}" is not one of ${CONDITION_WORDS}, so it was dropped.`,
        );
      }
    }

    if (typeof entry["category"] === "string") {
      const category = clampName(entry["category"]);
      if (category) proposal.category = category;
    }

    if (entry["priceDollars"] != null && entry["priceDollars"] !== "") {
      const cents = centsFromInput(String(entry["priceDollars"]));
      if (cents !== null) proposal.priceCents = cents;
      else {
        const said = String(entry["priceDollars"]).slice(0, 30);
        warnings.push(`"${name}": the price "${said}" is not a usable amount, so it was dropped.`);
      }
    }

    if (entry["tags"] != null) {
      if (Array.isArray(entry["tags"])) {
        const tags = normaliseTags(entry["tags"].filter((tag): tag is string => typeof tag === "string"));
        if (tags.length > 0) proposal.tags = tags;
      } else warnings.push(`"${name}": tags should be a list of words, so they were dropped.`);
    }

    if (entry["fields"] != null) {
      if (Array.isArray(entry["fields"])) {
        const labels = new Set<string>();
        const fields: { label: string; value: string }[] = [];
        for (const candidate of entry["fields"].slice(0, MAX_FIELDS_PER_ITEM)) {
          const pair = isRecord(candidate) ? candidate : {};
          const said = pair["value"];
          // A number is a fine value ("18" volts); an object is not.
          const field = normaliseField({
            label: pair["label"],
            value: typeof said === "string" || typeof said === "number" ? String(said) : "",
          });
          // A label with no value is noise here: it would ask a person to
          // accept an empty row.
          if (!field || !field.value || labels.has(field.label.toLowerCase())) continue;
          labels.add(field.label.toLowerCase());
          fields.push({ label: field.label, value: field.value });
        }
        if (fields.length > 0) proposal.fields = fields;
      } else
        warnings.push(`"${name}": fields should be a list of label and value pairs, so they were dropped.`);
    }

    if (Object.keys(proposal).length === 1) {
      warnings.push(`"${name}": the reply had nothing usable for it.`);
      return;
    }
    proposals.push(proposal);
  });

  for (const item of expected) {
    if (!seen.has(item.id)) warnings.push(`The reply has nothing for "${item.name}".`);
  }

  if (proposals.length === 0) {
    const detail = warnings.length ? ` ${warnings.join(" ")}` : "";
    return { ok: false, reason: `Nothing in that reply could be used.${detail}` };
  }

  return { ok: true, proposals, warnings };
}
